import {
    BaseAddressModel,
    BillToModel,
    ShipToModel,
    ValidatedAddressModel,
} from "@insite/client-framework/Types/ApiModels";
import { AddressValidationApiParameter } from "@MasterBluePrint/Services/PiyoviService";

/**
 * Shared Piyovi address-validation helpers.
 *
 * These centralize the guard / mapping logic that also lives inline in
 * `Handlers/ValidateAddressHandler.ts` (the main checkout-continue flow) so the
 * "Add New Address" flow in `ShippingAddress.tsx` can validate the same way
 * without re-running the framework's Redux-bound ValidateAddress chain.
 */

/**
 * Piyovi can only validate a complete US/CA address. Mirrors the guard in
 * `ValidateAddressHandler.ts` — returns false when any required field is missing
 * or the country is not US/CA, in which case the caller should save without a
 * validation prompt.
 */
export const isPiyoviValidatable = (address: BaseAddressModel): boolean => {
    const { address1, city, state, postalCode, country } = address;

    if (
        !address1?.trim() ||
        !city?.trim() ||
        !state?.abbreviation?.trim() ||
        !postalCode?.trim() ||
        !country?.abbreviation?.trim()
    ) {
        return false;
    }

    return country.abbreviation === "US" || country.abbreviation === "CA";
};

/** Maps a form address onto the Piyovi request parameter shape. */
export const toPiyoviApiParameter = (address: BaseAddressModel): AddressValidationApiParameter => ({
    companyName: address.companyName,
    address1: address.address1,
    address2: address.address2,
    city: address.city,
    stateProvince: address.state?.abbreviation,
    zipPostalCode: address.postalCode,
    country: address.country?.abbreviation,
});

/** Normalized snapshot of what the customer entered, for display in the modal. */
export const toEnteredValidatedAddress = (address: BaseAddressModel): ValidatedAddressModel => ({
    address1: address.address1,
    address2: address.address2,
    city: address.city,
    stateProvince: address.state?.abbreviation,
    zipPostalCode: address.postalCode,
    country: address.country?.abbreviation,
});

/**
 * Normalizes an address field for equality comparison: uppercases, treats
 * punctuation as a separator, and collapses/trims whitespace. So "123 Main St."
 * and "123  main  st" compare equal, while "90210" and "90210-1234" do not
 * (the extra +4 digits survive → meaningful difference).
 */
const normalizeAddressField = (value?: string): string =>
    (value ?? "")
        .replace(/[^\p{L}\p{N}\s]/gu, " ") // punctuation -> space
        .replace(/\s+/g, " ")
        .trim()
        .toUpperCase();

/**
 * True when the entered and suggested addresses are identical apart from case,
 * whitespace, and punctuation. Used to suppress the confirmation modal when
 * Piyovi's standardized suggestion is only a formatting variant of what the
 * customer entered — in that case there is nothing meaningful to confirm, so the
 * caller should continue the normal flow. A ZIP+4 extension is treated as a
 * meaningful difference (the extra digits do not normalize away).
 */
export const areValidatedAddressesEquivalent = (
    entered: ValidatedAddressModel,
    suggested: ValidatedAddressModel,
): boolean =>
    (["address1", "address2", "city", "stateProvince", "zipPostalCode", "country"] as const).every(
        field => normalizeAddressField(entered[field]) === normalizeAddressField(suggested[field]),
    );

/**
 * Merges Piyovi's suggested/standardized address into a form address, resolving
 * `state` from the country's states by abbreviation and mapping
 * `zipPostalCode -> postalCode`. Moved from `CheckoutShippingAddresses.tsx`.
 */
export const buildMergedAddress = <T extends ShipToModel | BillToModel>(
    formAddress: T,
    suggested: ValidatedAddressModel,
): T => {
    const resolvedState = suggested.stateProvince
        ? formAddress.country?.states?.find(s => s.abbreviation === suggested.stateProvince) ?? formAddress.state
        : formAddress.state;

    return {
        ...formAddress,
        address1: suggested.address1 ?? formAddress.address1,
        address2: suggested.address2 ?? formAddress.address2,
        city: suggested.city ?? formAddress.city,
        postalCode: suggested.zipPostalCode ?? formAddress.postalCode,
        state: resolvedState,
    };
};
